import { config } from 'config';
export type { ApiResponse } from 'shared/types/api';

const API_BASE_URL = config.apiUrl;

/**
 * Centralized API Client
 *
 * Features:
 * - Automatic token injection
 * - Company context header injection
 * - Request deduplication (prevents duplicate simultaneous requests)
 * - Response caching (optional, configurable per request)
 * - Consistent error handling
 * - Request/Response interceptors
 */

// Types
export interface ApiRequestOptions {
  /** HTTP method */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Request body data */
  data?: any;
  /** URL query parameters */
  params?: Record<string, any>;
  /** Cache TTL in milliseconds (0 = no cache, default) */
  cacheTTL?: number;
  /** Skip request deduplication */
  skipDeduplication?: boolean;
  /** Custom headers */
  headers?: Record<string, string>;
  /** Skip auth token */
  skipAuth?: boolean;
  /** Timeout in ms */
  timeout?: number;
  /** Response type: 'json' (default) or 'blob' */
  responseType?: 'json' | 'blob';
}

export interface ApiError {
  success: false;
  error: string;
  statusCode: number;
  details?: any;
}

// In-flight request tracking for deduplication
const inFlightRequests = new Map<string, Promise<any>>();

// Simple cache implementation
interface CacheEntry {
  data: any;
  expiry: number;
}
const cache = new Map<string, CacheEntry>();

// Company context storage
let currentCompanyId: string | null = null;

/**
 * Set the current company context for all API requests.
 *
 * When the active company changes mid-session (eg. a future switchCompany
 * action), the React Query cache must be cleared — otherwise lists keyed
 * without an explicit tenant discriminator will surface the previous
 * tenant's data until `staleTime` elapses (currently 5 min). This emits a
 * `company-context-changed` event so AuthContext can call
 * `queryClient.clear()` from one place. Don't bypass this helper.
 */
export const setCompanyContext = (companyId: string | null) => {
  const previous = currentCompanyId;
  currentCompanyId = companyId;
  if (previous !== companyId) {
    // Drop the in-memory apiClient response cache on any tenant boundary
    // change. Without this, cached entries keyed by URL alone (list-style
    // endpoints that rely exclusively on the `X-Company-Context` header to
    // scope, e.g. `/companies`, `/companies/stats`) can serve tenant A's
    // response to tenant B for up to the cache TTL.
    clearCache();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('company-context-changed', {
          detail: { previous, current: companyId },
        }),
      );
    }
  }
};

/**
 * Get the current company context
 */
export const getCompanyContext = (): string | null => {
  return currentCompanyId;
};

/**
 * Clear the API cache (useful after mutations)
 */
export const clearCache = (pattern?: string) => {
  if (pattern) {
    // Clear cache entries matching pattern
    const keysToDelete: string[] = [];
    cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => cache.delete(key));

    // Also drop matching in-flight GETs. Without this, a GET kicked off just
    // before a mutation (e.g. a list fetch on mount, still in flight when a
    // dialog's onSuccess calls clearCache + refetch a moment later) stays
    // registered here until it resolves — the "refetch" call above just
    // dedupes onto that same pre-mutation promise instead of hitting the
    // network again, so the screen silently shows stale data until a manual
    // reload starts with no leftover in-flight state. Only the tracking
    // entry is dropped, not the underlying fetch — it still resolves for
    // whatever (now-abandoned) caller awaited it directly.
    const inFlightKeysToDelete: string[] = [];
    inFlightRequests.forEach((_, key) => {
      if (key.includes(pattern)) {
        inFlightKeysToDelete.push(key);
      }
    });
    inFlightKeysToDelete.forEach(key => inFlightRequests.delete(key));
  } else {
    cache.clear();
    inFlightRequests.clear();
  }
};

/**
 * Generate a cache key from request parameters
 */
const generateCacheKey = (endpoint: string, options: ApiRequestOptions): string => {
  const params = options.params ? JSON.stringify(options.params) : '';
  const data = options.data ? JSON.stringify(options.data) : '';
  // Prefix the active tenant id so list-style endpoints that rely solely on
  // the `X-Company-Context` header for scoping never collide across tenants.
  // `global` is used when no tenant is selected (e.g. platform-wide screens).
  const tenant = currentCompanyId || 'global';
  return `${tenant}:${options.method || 'GET'}:${endpoint}:${params}:${data}`;
};

/**
 * Get auth token from storage.
 * Exported so that services using raw fetch/axios (for FormData uploads,
 * blob downloads, etc.) can obtain the token without direct localStorage access.
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Decode JWT token without verification (to check expiration)
 */
const decodeToken = (token: string): { exp?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
};

/**
 * Check if token is expired or will expire soon (within 60 seconds)
 */
const isTokenExpiredOrExpiring = (token: string): boolean => {
  const payload = decodeToken(token);
  if (!payload?.exp) return true; // If we can't decode, assume expired

  const currentTime = Math.floor(Date.now() / 1000);
  const bufferSeconds = 60; // Consider expired if within 60 seconds of expiry

  return payload.exp <= currentTime + bufferSeconds;
};

/**
 * Build URL with query parameters
 */
const buildUrl = (endpoint: string, params?: Record<string, any>): string => {
  let url = `${API_BASE_URL}${endpoint}`;

  if (params) {
    const filteredParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        filteredParams[key] = String(value);
      }
    }
    if (Object.keys(filteredParams).length > 0) {
      const queryString = new URLSearchParams(filteredParams).toString();
      url += `?${queryString}`;
    }
  }

  return url;
};

/**
 * Create fetch request with timeout
 */
const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeout: number = 30000
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    // `fetch()` throws a bare TypeError (no HTTP response at all — not a
    // 4xx/5xx) when something outside our server rejected the request
    // before it got a response: a browser extension (ad blocker / privacy
    // tool), corporate security software, or a real connectivity outage.
    // We can't distinguish which from JS (fetch errors are opaque by
    // design), but in practice this almost always turns out to be an
    // extension blocking PATCH/DELETE as "suspicious" — confirmed
    // recurring pattern across unrelated endpoints. Scoped to non-GET:
    // a broken GET already shows up as a stuck/empty page, so this
    // specifically covers the silent-failure case (a button that just
    // does nothing) where a nudge actually helps.
    if (error instanceof TypeError && (options.method || 'GET') !== 'GET') {
      window.dispatchEvent(new CustomEvent('network-request-blocked'));
    }
    throw error;
  }
};

/**
 * Main API request function
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const {
    method = 'GET',
    data,
    params,
    cacheTTL = 0,
    skipDeduplication = false,
    headers = {},
    skipAuth = false,
    timeout = 30000,
    responseType = 'json',
  } = options;

  const cacheKey = generateCacheKey(endpoint, options);

  // Check cache for GET requests
  if (method === 'GET' && cacheTTL > 0) {
    const cached = cache.get(cacheKey);
    if (cached && cached.expiry > Date.now()) {
      return cached.data;
    }
  }

  // Check for in-flight duplicate requests (GET only)
  if (method === 'GET' && !skipDeduplication) {
    const inFlight = inFlightRequests.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }
  }

  // Build request
  const url = buildUrl(endpoint, params);

  const isFormData = data instanceof FormData;

  const requestHeaders: Record<string, string> = {
    // Omit Content-Type for FormData — browser sets multipart boundary automatically
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    // Belt-and-suspenders against PII showing up in HTTP caches / bfcache /
    // intermediary proxies. The sy_be should also send Cache-Control: no-store
    // on PII responses; this request-side header asks the browser not to cache
    // the response even if the server forgets to set it.
    ...(method === 'GET' ? { 'Cache-Control': 'no-store' } : {}),
    ...headers,
  };

  // Add auth token
  if (!skipAuth) {
    const token = getAuthToken();
    if (token) {
      // Check if token is expired before making the request
      if (isTokenExpiredOrExpiring(token)) {
        window.dispatchEvent(new CustomEvent('session-expired'));
        const apiError: ApiError = {
          success: false,
          error: 'Token inválido o expirado',
          statusCode: 401,
          details: { reason: 'token_expired_client_side' },
        };
        throw apiError;
      }
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  // Add company context header if set
  if (currentCompanyId) {
    requestHeaders['X-Company-Context'] = currentCompanyId;
  }

  const fetchOptions: RequestInit = {
    method,
    headers: requestHeaders,
  };

  if (data && method !== 'GET') {
    fetchOptions.body = isFormData ? data : JSON.stringify(data);
  }

  // Create the request promise
  const requestPromise = (async () => {
    try {
      const response = await fetchWithTimeout(url, fetchOptions, timeout);

      // Handle non-OK responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const apiError: ApiError = {
          success: false,
          error: errorData.error || errorData.message || `HTTP ${response.status}`,
          statusCode: response.status,
          details: errorData,
        };

        // Handle 401 - only trigger session expired for actual token/auth failures
        if (response.status === 401) {
          const errorMessage = errorData.error || errorData.message || '';
          // Only trigger session-expired for token-related errors, not other 401s
          const sessionExpiredErrors = [
            'Token inválido o expirado',
            'Token de autorización requerido',
            'Sesión inválida',
            'PASSWORD_CHANGED',
          ];
          const isSessionExpired = sessionExpiredErrors.some(
            e => errorMessage.includes(e) || errorData.code === e
          );

          if (isSessionExpired) {
            // Emit custom event to trigger session expired modal
            window.dispatchEvent(new CustomEvent('session-expired'));
          }
        }

        throw apiError;
      }

      // Check for token refresh header and update stored token.
      //
      // Trust model: we accept the refreshed JWT *without* verifying its
      // signature on the client. The token is treated as authoritative because
      // it was delivered over TLS from our own API origin (so the only entity
      // that could have set this header is sy_be with our signing key). HSTS
      // (see public/_headers) makes downgrade attacks impractical; the JWT is
      // re-verified server-side on every subsequent request, so a forged
      // header here can only break the local session, not escalate privilege.
      // We only validate `exp` here to avoid clobbering a still-valid token
      // with a stale-cached response.
      const refreshedToken = response.headers.get('X-Refreshed-Token');
      if (refreshedToken) {
        const refreshedPayload = decodeToken(refreshedToken);
        const isRefreshedTokenValid = refreshedPayload?.exp &&
          refreshedPayload.exp > Math.floor(Date.now() / 1000) + 60;

        if (isRefreshedTokenValid) {
          localStorage.setItem('token', refreshedToken);
        }
      }

      // Handle blob responses (e.g., PDF exports)
      if (responseType === 'blob') {
        const blobData = await response.blob();
        return blobData as T;
      }

      const responseData = await response.json();

      // Cache successful GET responses if cacheTTL is set
      if (method === 'GET' && cacheTTL > 0) {
        cache.set(cacheKey, {
          data: responseData,
          expiry: Date.now() + cacheTTL,
        });
      }

      return responseData as T;
    } finally {
      // Remove from in-flight tracking
      inFlightRequests.delete(cacheKey);
    }
  })();

  // Track in-flight request for deduplication
  if (method === 'GET' && !skipDeduplication) {
    inFlightRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
};

/**
 * Convenience methods
 */
export const api = {
  get: <T = any>(endpoint: string, params?: Record<string, any>, options?: Omit<ApiRequestOptions, 'method' | 'params'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET', params }),

  post: <T = any>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method' | 'data'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'POST', data }),

  put: <T = any>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method' | 'data'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PUT', data }),

  patch: <T = any>(endpoint: string, data?: any, options?: Omit<ApiRequestOptions, 'method' | 'data'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PATCH', data }),

  delete: <T = any>(endpoint: string, options?: Omit<ApiRequestOptions, 'method'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),

  getBlob: (endpoint: string, params?: Record<string, any>, options?: Omit<ApiRequestOptions, 'method' | 'params' | 'responseType'>) =>
    apiRequest<Blob>(endpoint, { ...options, method: 'GET', params, responseType: 'blob' }),
};

/**
 * Raw fetch wrapper for code that needs the native Response object (blob
 * downloads, multipart uploads, file streaming) but still needs the same
 * auth + tenant headers that `apiRequest` injects.
 *
 * Returns the raw `Response`. Caller is responsible for `response.ok` checks
 * and parsing the body. Use this instead of calling `fetch()` directly so the
 * `X-Company-Context` header is never missed (multi-tenant data isolation
 * relies on it).
 */
export const rawFetchWithAuth = async (
  url: string,
  options: RequestInit = {},
): Promise<Response> => {
  const headers = new Headers(options.headers);
  const token = getAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (currentCompanyId && !headers.has('X-Company-Context')) {
    headers.set('X-Company-Context', currentCompanyId);
  }
  return fetch(url, { ...options, headers });
};

/**
 * Cached GET request (30 second default TTL)
 */
export const cachedGet = <T = any>(
  endpoint: string,
  params?: Record<string, any>,
  ttl: number = 30000
) =>
  api.get<T>(endpoint, params, { cacheTTL: ttl });

export default api;
